import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tab,
  Tabs,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  MoneyOff as MoneyOffIcon,
  Add as AddIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import apiClient from '../../../request/http-common';

const AdminJointAccountDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [newTransaction, setNewTransaction] = useState({
    type: 'deposit',
    amount: '',
    description: ''
  });
  const [balanceAdjustmentDialogOpen, setBalanceAdjustmentDialogOpen] = useState(false);
  const [balanceAdjustment, setBalanceAdjustment] = useState({
    adjustment_type: 'credit',
    amount: '',
    reason: '',
    description: ''
  });

  const { data: accountData, isLoading, error, refetch } = useQuery(
    ['adminJointAccount', id],
    async () => {
      const response = await apiClient.get(`/api/admin/joint-accounts/${id}`);
      return response.data;
    }
  );

  const { mutate: updateStatus, isLoading: isUpdating } = useMutation(
    async ({ status, reason }) => {
      const response = await apiClient.put(`/api/admin/joint-accounts/${id}/status`, {
        status,
        reason
      });
      return response.data;
    },
    {
      onSuccess: () => {
        setActionDialogOpen(false);
        setActionReason('');
        refetch();
      }
    }
  );

  const { mutate: createTransaction, isLoading: isCreatingTransaction } = useMutation(
    async (transactionData) => {
      const response = await apiClient.post(`/api/joint-accounts/${id}/transactions`, {
        transaction_type: transactionData.type,
        amount: parseFloat(transactionData.amount),
        description: transactionData.description || `Admin ${transactionData.type}`,
        metadata: {
          admin_created: true,
          admin_note: transactionData.description
        }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        setTransactionDialogOpen(false);
        setNewTransaction({ type: 'deposit', amount: '', description: '' });
        refetch();
      }
    }
  );

  const { mutate: adjustBalance, isLoading: isAdjustingBalance } = useMutation(
    async (adjustmentData) => {
      const response = await apiClient.post(`/api/admin/joint-accounts/${id}/adjust-balance`, {
        adjustment_type: adjustmentData.adjustment_type,
        amount: parseFloat(adjustmentData.amount),
        reason: adjustmentData.reason,
        description: adjustmentData.description
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        setBalanceAdjustmentDialogOpen(false);
        setBalanceAdjustment({
          adjustment_type: 'credit',
          amount: '',
          reason: '',
          description: ''
        });
        refetch();
        // Show success message
        localStorage.setItem('notification', JSON.stringify({
          message: data.message,
          type: 'success'
        }));
        const event = new Event('newMessage');
        window.dispatchEvent(event);
      },
      onError: (error) => {
        // Show error message
        const errorMessage = error.response?.data?.message || 'Failed to adjust balance';
        localStorage.setItem('notification', JSON.stringify({
          message: errorMessage,
          type: 'error'
        }));
        const event = new Event('newMessage');
        window.dispatchEvent(event);
      }
    }
  );

  const account = accountData?.joint_account;
  const stats = accountData?.statistics;

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'suspended': return 'warning';
      case 'closed': return 'error';
      default: return 'default';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner': return '#F7931A';
      case 'admin': return '#10B981';
      case 'member': return '#3B82F6';
      case 'viewer': return '#6C7293';
      default: return '#6C7293';
    }
  };

  const handleStatusChange = (newStatus) => {
    setActionType(newStatus);
    setActionDialogOpen(true);
  };

  const handleConfirmStatusChange = () => {
    if (!actionReason.trim()) return;
    updateStatus({ status: actionType, reason: actionReason });
  };

  const handleCreateTransaction = () => {
    if (!newTransaction.amount || parseFloat(newTransaction.amount) <= 0) return;
    createTransaction(newTransaction);
  };

  const handleBalanceAdjustment = () => {
    if (!balanceAdjustment.amount || parseFloat(balanceAdjustment.amount) <= 0) return;
    if (!balanceAdjustment.reason.trim()) return;
    adjustBalance(balanceAdjustment);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load joint account details.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton 
          onClick={() => navigate('/admin/joint-accounts')}
          sx={{ color: '#F7931A' }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFFFFF', flex: 1 }}>
          {account?.account_name}
        </Typography>
        <Chip
          label={account?.status}
          color={getStatusColor(account?.status)}
          sx={{ fontSize: '0.875rem' }}
        />
      </Box>

      {/* Admin Actions */}
      <Card sx={{
        background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
        border: '1px solid #2B3139',
        mb: 3
      }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 2 }}>
            Admin Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setTransactionDialogOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
              }}
            >
              Add Transaction
            </Button>
            <Button
              variant="contained"
              startIcon={<AccountBalanceIcon />}
              onClick={() => setBalanceAdjustmentDialogOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #F7931A 0%, #E8851A 100%)'
              }}
            >
              Adjust Balance
            </Button>
            {account?.status === 'active' && (
              <Button
                variant="outlined"
                startIcon={<BlockIcon />}
                onClick={() => handleStatusChange('suspended')}
                sx={{ borderColor: '#FFB800', color: '#FFB800' }}
              >
                Suspend Account
              </Button>
            )}
            {account?.status === 'suspended' && (
              <Button
                variant="outlined"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleStatusChange('active')}
                sx={{ borderColor: '#10B981', color: '#10B981' }}
              >
                Activate Account
              </Button>
            )}
            {account?.status !== 'closed' && (
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => handleStatusChange('closed')}
                sx={{ borderColor: '#F84960', color: '#F84960' }}
              >
                Close Account
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Account Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccountBalanceIcon sx={{ fontSize: 48, color: '#F7931A', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#10B981', fontWeight: 700, mb: 1 }}>
                {formatPrice(account?.balance)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Current Balance
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: 48, color: '#3B82F6', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
                {stats?.member_count || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Total Members
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AddIcon sx={{ fontSize: 48, color: '#10B981', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#10B981', fontWeight: 700, mb: 1 }}>
                {formatPrice(stats?.total_deposits)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Total Deposits
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <MoneyOffIcon sx={{ fontSize: 48, color: '#F84960', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#F84960', fontWeight: 700, mb: 1 }}>
                {formatPrice(stats?.total_withdrawals)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Total Withdrawals
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ 
        background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
        border: '1px solid #2B3139',
        mb: 3
      }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              color: '#6C7293',
              '&.Mui-selected': { color: '#F7931A' }
            },
            '& .MuiTabs-indicator': { backgroundColor: '#F7931A' }
          }}
        >
          <Tab label="Account Details" />
          <Tab label="Members" />
          <Tab label="Transactions" />
          <Tab label="Activity Log" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Card sx={{
          background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
          border: '1px solid #2B3139'
        }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 3 }}>
              Account Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                  Account Number
                </Typography>
                <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
                  #{account?.account_number}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                  Owner
                </Typography>
                <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
                  {account?.creator?.name || account?.creator?.email}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                  Approval Type
                </Typography>
                <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
                  {account?.approval_type?.replace('_', ' ').toUpperCase()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                  Minimum Approval Amount
                </Typography>
                <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
                  {formatPrice(account?.minimum_approval_amount)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                  Created Date
                </Typography>
                <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
                  {new Date(account?.created_at).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                  Pending Transactions
                </Typography>
                <Typography variant="body1" sx={{ color: '#FFB800', mb: 2 }}>
                  {stats?.pending_transactions || 0}
                </Typography>
              </Grid>
              {account?.description && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                    {account.description}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {currentTab === 1 && (
        <Card sx={{
          background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
          border: '1px solid #2B3139'
        }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 2 }}>
              Account Members ({account?.members?.length || 0})
            </Typography>
            <List>
              {account?.members?.map((member, index) => (
                <React.Fragment key={member.id}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ 
                        bgcolor: getRoleColor(member.role),
                        width: 48,
                        height: 48
                      }}>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ color: '#FFFFFF' }}>
                            {member.user?.name || member.user?.email}
                          </Typography>
                          <Chip
                            label={member.role}
                            size="small"
                            sx={{
                              backgroundColor: getRoleColor(member.role),
                              color: '#FFFFFF',
                              fontSize: '0.75rem'
                            }}
                          />
                          {member.status === 'pending' && (
                            <Chip
                              label="Pending"
                              size="small"
                              color="warning"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ color: '#6C7293' }}>
                          {member.user?.email}
                          {member.joined_at && (
                            <>
                              {' • '}
                              Joined {new Date(member.joined_at).toLocaleDateString()}
                            </>
                          )}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < (account?.members?.length || 0) - 1 && (
                    <Divider sx={{ borderColor: '#2B3139' }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {currentTab === 2 && (
        <Card sx={{
          background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
          border: '1px solid #2B3139'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                Transaction History
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate(`/admin/joint-accounts/transactions?account=${id}`)}
                sx={{ borderColor: '#F7931A', color: '#F7931A' }}
              >
                View All Transactions
              </Button>
            </Box>
            
            {account?.transactions?.length > 0 ? (
              <List>
                {account.transactions.slice(0, 10).map((transaction, index) => (
                  <React.Fragment key={transaction.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="subtitle1" sx={{ color: '#FFFFFF' }}>
                                {transaction.transaction_type?.charAt(0).toUpperCase() + transaction.transaction_type?.slice(1)}
                              </Typography>
                              <Chip
                                label={transaction.status}
                                size="small"
                                color={transaction.status === 'completed' ? 'success' : 
                                       transaction.status === 'pending' ? 'warning' : 'error'}
                              />
                            </Box>
                            <Typography variant="subtitle1" sx={{ 
                              color: transaction.transaction_type === 'deposit' ? '#10B981' : '#F84960',
                              fontWeight: 600
                            }}>
                              {transaction.transaction_type === 'deposit' ? '+' : '-'}{formatPrice(transaction.amount)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ color: '#6C7293' }}>
                              {transaction.description || 'No description'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6C7293' }}>
                              {new Date(transaction.created_at).toLocaleString()} • 
                              Initiated by: {transaction.initiator?.name || transaction.initiator?.email}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < Math.min(9, (account?.transactions?.length || 0) - 1) && (
                      <Divider sx={{ borderColor: '#2B3139' }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <HistoryIcon sx={{ fontSize: 64, color: '#6C7293', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 1 }}>
                  No Transactions Yet
                </Typography>
                <Typography variant="body2" sx={{ color: '#6C7293' }}>
                  This account has no transaction history
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Status Change Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFFFFF', borderBottom: '1px solid #2B3139' }}>
          Change Account Status
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
            Change account status to: <strong>{actionType}</strong>
          </Typography>
          
          <TextField
            fullWidth
            label="Reason for status change"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmStatusChange}
              disabled={!actionReason.trim() || isUpdating}
              variant="contained"
              sx={{ background: 'linear-gradient(135deg, #F7931A 0%, #FFB800 100%)' }}
            >
              {isUpdating ? <CircularProgress size={20} /> : 'Confirm'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Add Transaction Dialog */}
      <Dialog
        open={transactionDialogOpen}
        onClose={() => setTransactionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFFFFF', borderBottom: '1px solid #2B3139' }}>
          Add Admin Transaction
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Transaction Type</InputLabel>
            <Select
              value={newTransaction.type}
              onChange={(e) => setNewTransaction({...newTransaction, type: e.target.value})}
            >
              <MenuItem value="deposit">Deposit</MenuItem>
              <MenuItem value="withdrawal">Withdrawal</MenuItem>
              <MenuItem value="adjustment">Balance Adjustment</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={newTransaction.amount}
            onChange={(e) => setNewTransaction({...newTransaction, amount: e.target.value})}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            label="Description"
            value={newTransaction.description}
            onChange={(e) => setNewTransaction({...newTransaction, description: e.target.value})}
            multiline
            rows={3}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setTransactionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateTransaction}
              disabled={!newTransaction.amount || isCreatingTransaction}
              variant="contained"
              sx={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
            >
              {isCreatingTransaction ? <CircularProgress size={20} /> : 'Add Transaction'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Balance Adjustment Dialog */}
      <Dialog
        open={balanceAdjustmentDialogOpen}
        onClose={() => setBalanceAdjustmentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFFFFF', borderBottom: '1px solid #2B3139' }}>
          Adjust Joint Account Balance
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Adjustment Type</InputLabel>
            <Select
              value={balanceAdjustment.adjustment_type}
              onChange={(e) => setBalanceAdjustment({...balanceAdjustment, adjustment_type: e.target.value})}
            >
              <MenuItem value="credit">Credit (Add Funds)</MenuItem>
              <MenuItem value="debit">Debit (Remove Funds)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={balanceAdjustment.amount}
            onChange={(e) => setBalanceAdjustment({...balanceAdjustment, amount: e.target.value})}
            sx={{ mb: 3 }}
            inputProps={{ min: 0.01, step: 0.01 }}
          />

          <TextField
            fullWidth
            label="Reason (Required)"
            value={balanceAdjustment.reason}
            onChange={(e) => setBalanceAdjustment({...balanceAdjustment, reason: e.target.value})}
            multiline
            rows={2}
            sx={{ mb: 3 }}
            required
          />

          <TextField
            fullWidth
            label="Description (Optional)"
            value={balanceAdjustment.description}
            onChange={(e) => setBalanceAdjustment({...balanceAdjustment, description: e.target.value})}
            multiline
            rows={2}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setBalanceAdjustmentDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBalanceAdjustment}
              disabled={!balanceAdjustment.amount || !balanceAdjustment.reason.trim() || isAdjustingBalance}
              variant="contained"
              sx={{ background: 'linear-gradient(135deg, #F7931A 0%, #E8851A 100%)' }}
            >
              {isAdjustingBalance ? <CircularProgress size={20} /> : 'Adjust Balance'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AdminJointAccountDetails; 